import { UserRole } from "@/lib/auth-context"
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Card } from "@/components/ui/card"
import { Calendar, LayoutDashboard } from "lucide-react"

interface RoleSelectionDialogProps {
  open: boolean
  onRoleSelect: (role: UserRole) => void
}

/**
 * RoleSelectionDialog Component
 *
 * Allows users with multiple roles to select their preferred view.
 * Displays two clear options with icons and descriptions.
 *
 * @param open - Whether the dialog is open
 * @param onRoleSelect - Callback when a role is selected
 */
export function RoleSelectionDialog({ open, onRoleSelect }: RoleSelectionDialogProps) {
  return (
    <Dialog open={open}>
      <DialogContent className="sm:max-w-2xl" showCloseButton={false}>
        <DialogHeader>
          <DialogTitle className="text-2xl">Select Your View</DialogTitle>
          <DialogDescription>
            You have access to multiple roles. Please select which view you'd like to use.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
          {/* Admin Dashboard Option */}
          <Card
            className="p-6 hover:border-primary cursor-pointer transition-colors"
            onClick={() => onRoleSelect("admin")}
          >
            <div className="flex flex-col items-center text-center space-y-4">
              <div className="h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center">
                <LayoutDashboard className="h-8 w-8 text-primary" />
              </div>
              <div>
                <h3 className="text-lg font-semibold mb-2">Admin Dashboard</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  View and manage all employees' remote work schedules. Filter by employee and see team-wide
                  statistics.
                </p>
                <Button variant="default" className="w-full">
                  Select Admin View
                </Button>
              </div>
            </div>
          </Card>

          {/* Employee Calendar Option */}
          <Card
            className="p-6 hover:border-primary cursor-pointer transition-colors"
            onClick={() => onRoleSelect("employee")}
          >
            <div className="flex flex-col items-center text-center space-y-4">
              <div className="h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center">
                <Calendar className="h-8 w-8 text-primary" />
              </div>
              <div>
                <h3 className="text-lg font-semibold mb-2">Employee Calendar</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Manage your personal remote work schedule. Select days you plan to work remotely and track your
                  monthly remote days.
                </p>
                <Button variant="default" className="w-full">
                  Select Employee View
                </Button>
              </div>
            </div>
          </Card>
        </div>

        <div className="mt-4 text-center text-sm text-muted-foreground">
          You can switch between views anytime by logging out and selecting a different role.
        </div>
      </DialogContent>
    </Dialog>
  )
}

